import { createSelector } from '@reduxjs/toolkit';
import { AnyFMColumn, Column, FileManagerRow, FileSizeObject } from '../model';

import filesize from 'filesize';
import moment from 'moment';
import { TCondition, Type } from '../../../../general/modules/filter/dataModel';
import { Page } from '../../../models/page';
import { RootSelector } from '../../../models/rootSelector';
import { getPage, getSearchParams } from '../../app/selectors';
import { secondaryColumns } from '../secondaryColumns';
import { getActiveFilters, getNodes } from './selectors';
import { creativeNameCol } from '../baseTableColumns';
import { nameCol } from '../baseTableColumns/nameCol';

const filterableColumns = [...secondaryColumns.map((sc) => sc.column), creativeNameCol, nameCol];

export const getNodesFiltered: RootSelector<FileManagerRow[]> = createSelector(
  [getNodes, getActiveFilters],
  (rows, filters) => {
    return rows.filter((row) => {
      const filterPairs = Object.entries(filters) as [Column, TCondition][];
      if (filterPairs.length === 0) return true;

      const mappedPairs: [AnyFMColumn, TCondition][] = [];

      filterPairs.forEach(([col, condition]) => {
        const column = filterableColumns.find((x) => x.key === col);
        if (column === undefined) {
          return;
        }
        const v: [AnyFMColumn, TCondition] = [column, condition];

        mappedPairs.push(v);
      });

      let ok = true;
      for (const [column, condition] of mappedPairs) {
        const value = column.getter(row);
        const { from, ge, le, text, to } = condition;
        if (condition.enum && condition.enum.length) {
          // Если у колонки есть `multiGetter`, сравниваем по пересечению
          // массива значений с выбранными id (например, у креатива
          // несколько жанров — проходит, если хотя бы один из его жанров
          // попадает в выбранный набор). Иначе — старая скалярная логика.
          if (column.multiGetter) {
            const many = column.multiGetter(row);
            const enumSet = new Set(condition.enum as unknown[]);
            if (!many.some((v) => enumSet.has(v as unknown))) {
              ok = false;
              continue;
            }
          } else if (!condition.enum.includes(value as any)) {
            ok = false;
            continue;
          }
        }
        if (from && value !== null && from > value) {
          ok = false;
          continue;
        }
        if (to && value !== null && moment(to).add(1, 'day').toDate() < value) {
          ok = false;
          continue;
        }

        if (le && value !== null && le < (value as number)) {
          if (column.filter === Type.BYTES) {
            // коррекция  того, что в интерфейсе оно показывается с 2 знаками после запятой, т.е. допускаю 1% погрешность
            const fsizeLe = filesize(le, { output: 'object' }) as unknown as FileSizeObject;
            const fsizeValue = filesize(value as number, {
              output: 'object',
            }) as unknown as FileSizeObject;
            const leComputed = fsizeLe.value * 1024 ** fsizeLe.exponent;
            const valueComputed = fsizeValue.value * 1024 ** fsizeValue.exponent;

            if (leComputed < valueComputed) {
              ok = false;
            }
            continue;
          }
          ok = false;
          continue;
        }
        if (ge && value !== null && ge > (value as number)) {
          if (column.filter === Type.BYTES) {
            // коррекция  того, что в интерфейсе оно показывается с 2 знаками после запятой, т.е. допускаю 1% погрешность
            const fsizeGe = filesize(ge, { output: 'object' }) as unknown as FileSizeObject;
            const fsizeValue = filesize(value as number, {
              output: 'object',
            }) as unknown as FileSizeObject;
            const geComputed = fsizeGe.value * 1024 ** fsizeGe.exponent;
            const valueComputed = fsizeValue.value * 1024 ** fsizeValue.exponent;

            if (geComputed > valueComputed) {
              ok = false;
            }
            continue;
          }
          ok = false;
          continue;
        }
        // MYUA-17039: отбрасываем строку, если подстроки в значении НЕТ (раньше
        // условие было инвертировано — ветка не работала ни у одной колонки).
        // Регистр не важен — как в поиске по списку у `FilterEnumContainer`.
        if (
          text &&
          !(column.toDisplayString(row) ?? '')
            .toLocaleLowerCase()
            .includes(text.toLocaleLowerCase())
        ) {
          ok = false;
          continue;
        }
      }
      return ok;
    });
  },
);
export const getIgnoreFilters = createSelector([getPage, getSearchParams], (page, searchParams) => {
  return page === Page.SEARCH && searchParams && searchParams.scope === null;
});
